import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { COLORS } from 'src/utils/Themes/colors'
import CloseButton from 'src/components/common/CloseButton'
import {
  Box,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@material-ui/core'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import TabContext from '@mui/lab/TabContext'
import RenewalPolicies from 'src/components/RiskReports/UpComingRenewals/RenewalPolicies'
import { RENEWAL_TABS } from 'src/utils/constants/common'
import { downloadUpcomingRenewalsExcel } from 'src/services/individualService'
import { TAB_TITLES } from 'src/utils/constants/RiskReports'

const AllRenewals = ({ closeModal, aggregatedPolicies, ClientId }) => {
  const classes = useStyles()
  const [tabValue, setTabValue] = useState(RENEWAL_TABS?.NEAR_TO_EXPIRE)
  const [insuranceFilter, setInsuranceFilter] = useState('All')

  const tabs = [
    RENEWAL_TABS?.NEAR_TO_EXPIRE,
    RENEWAL_TABS?.ACTIVE,
    RENEWAL_TABS?.EXPIRED,
  ]

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  const getTabName = (tabName) => {
    switch (tabName) {
      case RENEWAL_TABS?.NEAR_TO_EXPIRE:
        return TAB_TITLES?.NEAR_TO_EXPIRE
      case RENEWAL_TABS?.EXPIRED:
        return TAB_TITLES?.EXPIRED
      default:
        return TAB_TITLES?.ACTIVE
    }
  }

  const getGroupedPolicies = (tabType) => {
    switch (tabType) {
      case RENEWAL_TABS?.NEAR_TO_EXPIRE:
        return aggregatedPolicies?.nearToExpirePolicies
      case RENEWAL_TABS?.EXPIRED:
        return aggregatedPolicies?.expiredPolicies
      default:
        return aggregatedPolicies?.activePolicies
    }
  }

  const downloadExcel = async () => {
    const insuranceParam = insuranceFilter === 'All' ? null : insuranceFilter

    await downloadUpcomingRenewalsExcel(ClientId, insuranceParam)
  }

  return (
    <>
      <div className={classes.root}>
        <CloseButton
          title='Close'
          classname={classes.clsBtn}
          handleClose={closeModal}
        />
        <h2 className={classes.h2title}>Policies</h2>
        <span className={classes.downloadExcelText} onClick={downloadExcel}>
          Download Excel
        </span>

        {/* Keep layout unchanged: place dropdown below the download link so header alignment isn't modified */}
        <div className={classes.dropdownWrapper}>
          <FormControl
            className={classes.selectControl}
            variant='outlined'
            size='small'
          >
            <InputLabel id='insurance-filter-label'>Insurance</InputLabel>
            <Select
              labelId='insurance-filter-label'
              id='insurance-filter'
              value={insuranceFilter}
              onChange={(e) => setInsuranceFilter(e.target.value)}
              label='Insurance'
            >
              <MenuItem value={'All'} className={classes.menuItem}>
                All
              </MenuItem>
              <MenuItem value={'Motor Insurance'} className={classes.menuItem}>
                Motor
              </MenuItem>
            </Select>
          </FormControl>
        </div>

        <div className={classes.rootContent}>
          <TabContext value={tabValue}>
            <Box>
              <TabList id='tabList' onChange={handleTabChange}>
                {tabs?.map((tabListItem) => (
                  <Tab
                    key={`tab_${tabListItem}`}
                    label={getTabName(tabListItem)}
                    value={tabListItem}
                    wrapped
                    className={
                      tabValue === tabListItem
                        ? classes.tabSelected
                        : classes.tab
                    }
                  />
                ))}
              </TabList>
            </Box>
            {tabs?.map((tabListItem) => (
              <TabPanel
                key={`tabPanel_${tabListItem}`}
                value={tabListItem}
                className={classes.tabPanel}
              >
                <RenewalPolicies
                  groupedPolicies={getGroupedPolicies(tabListItem)}
                />
              </TabPanel>
            ))}
          </TabContext>
        </div>
      </div>
    </>
  )
}

export default AllRenewals

const useStyles = makeStyles({
  root: {
    padding: '0px',
    position: 'relative',
  },
  rootContent: {
    width: '100%',
  },
  cardItem: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    boxSizing: 'border-box',
    margin: '0px 10px 20px',
    width: 210,
    borderRadius: '10px',
    boxShadow:
      '0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)',
    border: '1px solid #e5e5e5',
    minHeight: '48px',
    transition: 'box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    '&:hover': {
      borderColor: '#cdcdcd',
      boxShadow:
        'rgb(0 0 0 / 20%) 0px 3px 3px -2px, rgb(0 0 0 / 14%) 0px 3px 4px 0px, rgb(0 0 0 / 12%) 0px 1px 8px 0px',
    },
  },
  rowFlex: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '12px 16px',
    width: '100%',
    boxSizing: 'border-box',
    height: '60px',
  },
  divider: {
    borderBottom: '1px solid #e5e5e5',
    height: '1px',
    width: '100%',
  },
  icon: {
    width: '24px',
    marginRight: '10px',
    flexShrink: 0,
    '& img': {
      maxWidth: '100%',
    },
  },
  title: {
    fontSize: '14px',
    lineHeight: '18px',
    color: COLORS.GREY,
  },
  cardFooter: {
    fontSize: '11px',
    color: '#112F3C99',
    flexGrow: 1,
  },
  cardFooterText: {
    color: COLORS.GREY,
    fontSize: '12px',
    marginTop: '3px',
    letterSpacing: '0.8px',
  },
  alignLeft: {
    textAlign: 'left',
    '@media (max-width: 600px)': {
      textAlign: 'center',
    },
  },
  alignRight: {
    textAlign: 'right',
    '@media (max-width: 600px)': {
      textAlign: 'center',
    },
  },
  clsBtn: {
    position: 'absolute',
    right: 0,
  },
  h2title: {
    fontSize: '22px',
    marginTop: 0,
    fontWeight: 500,
  },
  selectControl: {
    minWidth: '100px',
    width: '100px',
    '& .MuiSelect-select': {
      textAlign: 'left',
    },
    '& .MuiMenuItem-root': {
      paddingLeft: '20px',
    },
  },
  menuItem: {
    fontSize: '14px',
    paddingLeft: '20px !important',
    paddingRight: '16px',
    paddingTop: '10px',
    paddingBottom: '10px',
  },
  tab: {
    textTransform: 'unset',
    fontSize: '15px',
    color: COLORS.GREY,
    paddingTop: '17px',
    minWidth: '10vw',
  },
  tabSelected: {
    textTransform: 'unset',
    fontSize: '16px',
    color: COLORS.DARK_PINK,
    fontWeight: '550',
    paddingTop: '17px',
    minWidth: '10vw',
  },
  tabPanel: {
    padding: '10px',
  },
  downloadExcelText: {
    textAlign: 'end',
    color: COLORS.LINK_COLOR_DARK,
    width: '100%',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  dropdownWrapper: {
    marginTop: '8px',
  },
})
